import { Icon } from "@uzverse/ui";
import type { TaskState } from "@uzverse/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { shortModelName, useModelCatalog } from "../state/modelPolicy.js";
import { TASK_PROGRESS } from "../state/store.js";
import type { TaskItem } from "../state/store.js";

const FLOW: TaskState[] = ["received", "routing", "planning", "in_progress", "review", "done"];

/** Where the task is in `FLOW`; approval waits happen during execution. */
function position(state: TaskState): number {
  switch (state) {
    case "awaiting_approval":
      return 3;
    case "needs_owner":
    case "cancelled":
      return 4;
    default:
      return FLOW.indexOf(state);
  }
}

/**
 * What the assistant is doing for one task: a compact summary line that expands into the steps
 * taken so far. The label of the current step is the task's real state.
 */
export function TaskSteps({ task, running }: { task: TaskItem; running: boolean }) {
  const { t, i18n } = useTranslation("web");
  const [open, setOpen] = useState(false);
  const catalog = useModelCatalog();
  const locale = i18n.language === "en" ? "en" : "tr";
  const modelName = task.model
    ? shortModelName(catalog?.models.find((m) => m.id === task.model)?.label[locale] ?? task.model)
    : "";
  const at = position(task.state);
  const failed = task.state === "needs_owner" || task.state === "cancelled";
  const steps = FLOW.map((state, i) => {
    const label = i === at && task.state !== state ? task.state : state;
    const status =
      failed && i === at
        ? "stopped"
        : i < at || task.state === "done"
          ? "done"
          : i === at
            ? "current"
            : "todo";
    return { state: label, status } as const;
  });
  return (
    <div className={`steps-card${running ? " is-running" : ""}`}>
      <button
        type="button"
        className="steps-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title={open ? t("chatUi.stepsHide") : t("chatUi.stepsShow")}
      >
        <Icon name="chevronRight" size={14} className="steps-chevron" />
        <span className="steps-title">{t("chatUi.steps")}</span>
        <span className="steps-bar" aria-hidden="true">
          <span style={{ width: `${TASK_PROGRESS[task.state]}%` }} data-state={task.state} />
        </span>
      </button>
      {open && (
        <div className="steps-body" role="group" aria-label={t("chatUi.stepsLabel")}>
          <ol className="steps-list">
            {steps.map((s) => (
              <li key={s.state + s.status} data-status={s.status}>
                <span className="step-mark" aria-hidden="true">
                  {s.status === "done" ? <Icon name="check" size={12} /> : null}
                </span>
                <span>{t(`task.state.${s.state}`)}</span>
              </li>
            ))}
          </ol>
          {task.detail && <p className="muted small">{task.detail}</p>}
          {task.model && (
            <p className="muted small" data-testid="task-model">
              {t("chatUi.modelUsed")}: {modelName}
              {task.effort && (
                <>
                  {" · "}
                  {t("chatUi.effortUsed")}: {t(`modelPick.effortLevel.${task.effort}.name`)}
                </>
              )}
            </p>
          )}
          {/* TODO: show the per-task cost once the realtime protocol reports it. */}
          <p className="muted small">
            {t("chatUi.cost")}: {t("chatUi.costUnknown")}
          </p>
        </div>
      )}
    </div>
  );
}
